import {
  ArrowsSplit,
  BookOpen,
  Bug,
  Calendar,
  ChatCircle,
  CheckSquare,
  Circle,
  Code,
  Compass,
  Cube,
  Database,
  Diamond,
  Fire,
  Flag,
  Flask,
  GearSix,
  GitBranch,
  Globe,
  Heart,
  Hexagon,
  type Icon,
  Kanban,
  Lightbulb,
  Lightning,
  ListChecks,
  Lock,
  Package,
  Palette,
  Plugs,
  Question,
  Rocket,
  Shield,
  Sparkle,
  Square,
  Stack,
  Star,
  Target,
  TreeStructure,
  Wrench,
} from "@phosphor-icons/react";
import { cx } from "@/lib/cx";
import { typeTint } from "@/lib/format";

/**
 * Item types store an icon *name* (string) chosen by the admin. Only names listed here can be rendered,
 * which keeps the bundle small (no `import *`). Add names here to make them available in the type editor.
 */
export const TYPE_ICONS = {
  Flag,
  Rocket,
  Stack,
  Lightning,
  Bug,
  CheckSquare,
  ListChecks,
  Lightbulb,
  Target,
  Compass,
  Cube,
  Wrench,
  BookOpen,
  Question,
  ChatCircle,
  GearSix,
  Shield,
  Star,
  Heart,
  Package,
  Palette,
  Code,
  Database,
  Globe,
  Calendar,
  Flask,
  Fire,
  Lock,
  TreeStructure,
  Diamond,
  Hexagon,
  Square,
  Circle,
  Kanban,
  ArrowsSplit,
  GitBranch,
  Plugs,
  Sparkle,
} satisfies Record<string, Icon>;

export type TypeIconName = keyof typeof TYPE_ICONS;
export const TYPE_ICON_NAMES = Object.keys(TYPE_ICONS) as TypeIconName[];

/**
 * Item type glyph. Each default type has a distinct *shape* (flag, stack, bolt, bug, ...), so types are
 * never told apart by colour alone. `label` is the type name; omit with `decorative` when printed beside it.
 * `color` is the type's stored palette name: it tints the glyph (`--type-*` tokens, >= 3:1 on every surface in
 * both themes) and wins over `className` colours; without it the glyph keeps `className` or `text-fg-muted`.
 */
export function TypeIcon({
  name,
  label,
  decorative,
  size = 14,
  color,
  className,
}: {
  name: string;
  label?: string;
  decorative?: boolean;
  size?: number;
  /** Stored type colour (palette name). */
  color?: string | null;
  className?: string;
}) {
  const I: Icon = (TYPE_ICONS as Record<string, Icon>)[name] ?? Square;
  const tint = typeTint(color);
  const glyph = (
    <I
      size={size}
      aria-hidden="true"
      className={cx("shrink-0", className ?? (tint ? undefined : "text-fg-muted"))}
      style={tint ? { color: tint } : undefined}
    />
  );
  if (decorative || !label) return glyph;
  return (
    <span role="img" aria-label={label} title={label} className="inline-flex shrink-0">
      {glyph}
    </span>
  );
}
